import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Badge, Button, Calendar, Card, Col, DatePicker, Descriptions, Drawer,
  Form, Input, InputNumber, Modal, Popconfirm, Radio, Row, Select, Space, Spin,
  Table, Tag, TimePicker, Typography, message,
} from "antd";
import {
  CalendarOutlined, CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined,
  PlusOutlined, UnorderedListOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  createAppointment, deleteAppointment, getAppointments,
  getPatientHistoryByPin, respondAppointment, updateAppointment,
} from "../../services/doctorService";
import PatientHistoryView from "./PatientHistoryView";
import { handleApiError } from "../../utils/apiErrors";

const { Text, Title } = Typography;
const { Option } = Select;
const toArray = (d) => Array.isArray(d) ? d : (d?.results ?? []);

const STATUS_COLOR = {
  PENDING:          "orange",
  PENDING_APPROVAL: "gold",
  CONFIRMED:        "blue",
  COMPLETED:        "green",
  CANCELLED:        "default",
  REJECTED:         "red",
};
const STATUS_LABEL = {
  PENDING:          "Pending",
  PENDING_APPROVAL: "Awaiting Approval",
  CONFIRMED:        "Confirmed",
  COMPLETED:        "Completed",
  CANCELLED:        "Cancelled",
  REJECTED:         "Rejected",
};

export default function Appointments({ patientProfiles = [] }) {
  const [view,        setView]        = useState("calendar");  // "calendar" | "list" | "requests"
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [editing,     setEditing]     = useState(null);       // appointment for detail/edit
  const [newModal,    setNewModal]    = useState(false);
  const [newForm]     = Form.useForm();
  const [editForm]    = Form.useForm();

  // list filters
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterRange,  setFilterRange]  = useState(null);     // [dayjs, dayjs] | null
  const [filterPatient, setFilterPatient] = useState(null);

  const [historyDrawer, setHistoryDrawer] = useState(null);

  // patient-initiated requests
  const [approveModal, setApproveModal] = useState(null);   // appointment being approved
  const [approveForm]  = Form.useForm();
  const [rejectModal,  setRejectModal]  = useState(null);   // appointment being rejected
  const [rejectNotes,  setRejectNotes]  = useState("");

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAppointments();
      setRows(toArray(res.data));
    } catch {
      message.error("Failed to load appointments.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  // ── Create ────────────────────────────────────────────────────────────────
  const handleCreate = async (values) => {
    const scheduled_at = dayjs(values.date)
      .hour(dayjs(values.time).hour())
      .minute(dayjs(values.time).minute())
      .second(0)
      .toISOString();
    try {
      await createAppointment({
        patient:          values.patient,
        scheduled_at,
        duration_minutes: values.duration_minutes ?? 30,
        reason:           values.reason ?? "",
        status:           "PENDING",
      });
      message.success("Appointment created.");
      setNewModal(false);
      newForm.resetFields();
      loadAppointments();
    } catch (err) {
      handleApiError(err, "Failed to create appointment.");
    }
  };

  // ── Update / Status change ────────────────────────────────────────────────
  const openEdit = (appt) => {
    setEditing(appt);
    editForm.setFieldsValue({
      date:             dayjs(appt.scheduled_at),
      time:             dayjs(appt.scheduled_at),
      duration_minutes: appt.duration_minutes,
      reason:           appt.reason,
      status:           appt.status,
      notes:            appt.notes,
    });
  };

  const handleSave = async (values) => {
    if (!editing) return;
    const scheduled_at = dayjs(values.date)
      .hour(dayjs(values.time).hour())
      .minute(dayjs(values.time).minute())
      .second(0)
      .toISOString();
    try {
      await updateAppointment(editing.id, {
        scheduled_at,
        duration_minutes: values.duration_minutes,
        reason:           values.reason,
        status:           values.status,
        notes:            values.notes,
      });
      message.success("Appointment saved.");
      setEditing(null);
      loadAppointments();
    } catch {
      message.error("Failed to save appointment.");
    }
  };

  const changeStatus = async (appt, status) => {
    try {
      await updateAppointment(appt.id, { status });
      message.success(`Marked ${STATUS_LABEL[status] ?? status}.`);
      loadAppointments();
      if (editing?.id === appt.id) {
        setEditing({ ...editing, status });
        editForm.setFieldValue("status", status);
      }
    } catch {
      message.error("Failed to update appointment.");
    }
  };

  const handleDelete = async (appt) => {
    try {
      await deleteAppointment(appt.id);
      message.success("Appointment removed.");
      setEditing(null);
      loadAppointments();
    } catch {
      message.error("Failed to delete appointment.");
    }
  };

  const handleViewPatientHistory = async (pin) => {
    if (!pin) return;
    try {
      const res = await getPatientHistoryByPin(pin);
      setHistoryDrawer(res.data);
    } catch {
      message.error("Failed to load patient history.");
    }
  };

  // ── Respond to patient-requested appointments ─────────────────────────────
  const openApproveModal = (appt) => {
    setApproveModal(appt);
    // Pre-fill with the patient's preferred date/time
    approveForm.setFieldsValue({
      confirmed_date:    appt.preferred_date ? dayjs(appt.preferred_date) : null,
      confirmed_time:    appt.preferred_time ? dayjs(`2000-01-01T${appt.preferred_time}`) : null,
      duration_minutes:  30,
      notes:             "",
    });
  };

  const handleApprove = async (values) => {
    if (!approveModal) return;
    try {
      await respondAppointment(approveModal.id, {
        action:           "approve",
        confirmed_date:   dayjs(values.confirmed_date).format("YYYY-MM-DD"),
        confirmed_time:   dayjs(values.confirmed_time).format("HH:mm"),
        duration_minutes: values.duration_minutes,
        notes:            values.notes ?? "",
      });
      message.success("Appointment confirmed.");
      setApproveModal(null);
      approveForm.resetFields();
      loadAppointments();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.detail ?? d?.confirmed_date?.[0] ?? "Failed to confirm appointment.");
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      await respondAppointment(rejectModal.id, { action: "reject", notes: rejectNotes });
      message.success("Appointment request declined.");
      setRejectModal(null);
      setRejectNotes("");
      loadAppointments();
    } catch {
      message.error("Failed to decline appointment.");
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const byDate = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const k = dayjs(r.scheduled_at).format("YYYY-MM-DD");
      (map[k] = map[k] || []).push(r);
    });
    return map;
  }, [rows]);

  const filteredRows = useMemo(() => {
    let out = [...rows];
    if (filterStatus !== "ALL") {
      out = out.filter(r => r.status === filterStatus);
    }
    if (filterRange && filterRange[0] && filterRange[1]) {
      const [start, end] = filterRange;
      out = out.filter(r => {
        const d = dayjs(r.scheduled_at);
        return d.isAfter(start.startOf("day").subtract(1, "ms"))
          && d.isBefore(end.endOf("day").add(1, "ms"));
      });
    }
    if (filterPatient) {
      out = out.filter(r => r.patient === filterPatient);
    }
    return out.sort((a, b) => dayjs(b.scheduled_at).valueOf() - dayjs(a.scheduled_at).valueOf());
  }, [rows, filterStatus, filterRange, filterPatient]);

  const patientOptions = patientProfiles.map(p => ({
    value: p.id,
    label: `${p.user_email ?? "—"} · PIN ${p.pin ?? "—"}`,
  }));

  // ── Render — cell for calendar ────────────────────────────────────────────
  const cellRender = (value, info) => {
    if (info.type !== "date") return info.originNode;
    const key = value.format("YYYY-MM-DD");
    const items = byDate[key] ?? [];
    if (items.length === 0) return info.originNode;
    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 11 }}>
        {items.slice(0, 3).map(a => (
          <li key={a.id}
            onClick={(e) => { e.stopPropagation(); openEdit(a); }}
            style={{
              marginBottom: 2, padding: "1px 6px", borderRadius: 3,
              cursor: "pointer", color: "#fff",
              background: {
                CONFIRMED:        "#1677ff",
                PENDING:          "#fa8c16",
                PENDING_APPROVAL: "#d4b106",
                COMPLETED:        "#52c41a",
                CANCELLED:        "#bfbfbf",
                REJECTED:         "#f5222d",
              }[a.status] ?? "#8c8c8c",
              textDecoration: a.status === "CANCELLED" ? "line-through" : "none",
            }}>
            {dayjs(a.scheduled_at).format("HH:mm")} · {a.patient_pin ?? "—"}
          </li>
        ))}
        {items.length > 3 && (
          <li style={{ fontSize: 10, color: "#8c8c8c" }}>+ {items.length - 3} more</li>
        )}
      </ul>
    );
  };

  const listColumns = [
    { title: "Date / Time", dataIndex: "scheduled_at", key: "scheduled_at", width: 170,
      render: v => dayjs(v).format("MMM D, YYYY · h:mm A"),
      defaultSortOrder: "descend",
      sorter: (a, b) => dayjs(a.scheduled_at).valueOf() - dayjs(b.scheduled_at).valueOf() },
    { title: "Patient", key: "patient", render: r => (
        <span>
          <Text>{r.patient_email ?? "—"}</Text>
          {r.patient_pin && <> &nbsp;<Text code style={{ fontSize: 11 }}>{r.patient_pin}</Text></>}
        </span>
      ) },
    { title: "Reason", dataIndex: "reason", key: "reason", ellipsis: true,
      render: v => v || <Text type="secondary">—</Text> },
    { title: "Duration", dataIndex: "duration_minutes", key: "duration_minutes", width: 110,
      render: v => `${v ?? 30} min` },
    { title: "Status", dataIndex: "status", key: "status", width: 120,
      render: s => (
        <Tag color={STATUS_COLOR[s] ?? "default"}
          style={{ textDecoration: s === "CANCELLED" ? "line-through" : "none" }}>
          {STATUS_LABEL[s] ?? s}
        </Tag>
      ) },
    { title: "Actions", key: "actions", width: 240, render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>View</Button>
          {r.status !== "COMPLETED" && (
            <Button size="small" icon={<CheckOutlined />} type="primary" ghost
              onClick={() => changeStatus(r, "COMPLETED")}>
              Done
            </Button>
          )}
          {r.status !== "CANCELLED" && (
            <Popconfirm title="Cancel this appointment?" okText="Yes, Cancel"
              onConfirm={() => changeStatus(r, "CANCELLED")}>
              <Button size="small" icon={<CloseOutlined />} danger>Cancel</Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ];

  const pendingRequests = rows.filter(r => r.status === "PENDING_APPROVAL" && r.requested_by_patient);

  return (
    <>
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: "#262626" }}>Appointments</Title>
        </Col>
        <Col>
          <Space>
            <Radio.Group value={view} onChange={(e) => setView(e.target.value)} buttonStyle="solid">
              <Radio.Button value="calendar"><CalendarOutlined /> Calendar View</Radio.Button>
              <Radio.Button value="list"><UnorderedListOutlined /> List View</Radio.Button>
              <Radio.Button value="requests">
                <Badge count={pendingRequests.length} size="small" offset={[6, -2]}>
                  Pending Requests
                </Badge>
              </Radio.Button>
            </Radio.Group>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewModal(true)}>
              New Appointment
            </Button>
          </Space>
        </Col>
      </Row>

      {view === "calendar" ? (
        <Card variant="borderless" style={{ borderRadius: 8 }}>
          <Spin spinning={loading}>
            {!loading && rows.length === 0 && (
              <Alert
                type="info"
                showIcon
                message="No appointments scheduled"
                description="Click 'New Appointment' to book your first appointment with a patient."
                style={{ marginBottom: 12, borderRadius: 6 }}
              />
            )}
            <Calendar cellRender={cellRender}
              onSelect={(date) => {
                const key = date.format("YYYY-MM-DD");
                const items = byDate[key] ?? [];
                if (items.length === 1) openEdit(items[0]);
              }}
            />
            <Alert
              type="info"
              showIcon
              message="Legend"
              description={
                <Space wrap>
                  <Badge color="#1677ff" text="Confirmed" />
                  <Badge color="#fa8c16" text="Pending" />
                  <Badge color="#52c41a" text="Completed" />
                  <Badge color="#bfbfbf" text="Cancelled" />
                </Space>
              }
              style={{ marginTop: 12, borderRadius: 6 }}
            />
          </Spin>
        </Card>
      ) : (
        <Card variant="borderless" style={{ borderRadius: 8 }}>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} md={8}>
              <DatePicker.RangePicker style={{ width: "100%" }} value={filterRange}
                onChange={setFilterRange} />
            </Col>
            <Col xs={12} md={6}>
              <Select style={{ width: "100%" }} value={filterStatus} onChange={setFilterStatus}
                options={[
                  { value: "ALL",             label: "All Statuses" },
                  { value: "PENDING_APPROVAL",label: "Awaiting Approval" },
                  { value: "PENDING",         label: "Pending" },
                  { value: "CONFIRMED",       label: "Confirmed" },
                  { value: "COMPLETED",       label: "Completed" },
                  { value: "CANCELLED",       label: "Cancelled" },
                  { value: "REJECTED",        label: "Rejected" },
                ]} />
            </Col>
            <Col xs={24} md={10}>
              <Select showSearch allowClear style={{ width: "100%" }}
                placeholder="Filter by patient"
                optionFilterProp="label"
                value={filterPatient}
                onChange={setFilterPatient}
                options={patientOptions} />
            </Col>
          </Row>
          <Table
            rowKey="id"
            loading={loading}
            columns={listColumns}
            dataSource={filteredRows}
            pagination={{ pageSize: 20 }}
            locale={{
              emptyText: filterStatus !== "ALL" || filterRange || filterPatient
                ? "No appointments match your current filters. Try adjusting the date range or status filter."
                : "No appointments scheduled. Click 'New Appointment' to book one."
            }}
            size="small"
          />
        </Card>
      )}

      {view === "requests" && (
        <Card variant="borderless" style={{ borderRadius: 8 }}>
          {pendingRequests.length === 0 ? (
            <Alert type="info" showIcon message="No pending appointment requests from patients." />
          ) : (
            <Table
              rowKey="id"
              loading={loading}
              size="small"
              pagination={{ pageSize: 20 }}
              dataSource={pendingRequests}
              columns={[
                { title: "Patient Preferred Time", key: "preferred", width: 190,
                  render: r => r.preferred_date
                    ? `${r.preferred_date}${r.preferred_time ? ` at ${r.preferred_time.slice(0,5)}` : ""}`
                    : "—" },
                { title: "Patient", key: "patient",
                  render: r => (
                    <Space>
                      <Text>{r.patient_email ?? "—"}</Text>
                      {r.patient_pin && <Text code style={{ fontSize: 11 }}>{r.patient_pin}</Text>}
                    </Space>
                  ) },
                { title: "Reason", dataIndex: "reason", key: "reason", ellipsis: true,
                  render: v => v || <Text type="secondary">—</Text> },
                { title: "Patient Notes", dataIndex: "patient_notes", key: "patient_notes", ellipsis: true,
                  render: v => v || <Text type="secondary">—</Text> },
                { title: "Actions", key: "actions", width: 180,
                  render: (_, r) => (
                    <Space>
                      <Button size="small" type="primary" icon={<CheckOutlined />}
                        onClick={() => openApproveModal(r)}>
                        Approve
                      </Button>
                      <Button size="small" danger icon={<CloseOutlined />}
                        onClick={() => { setRejectModal(r); setRejectNotes(""); }}>
                        Decline
                      </Button>
                    </Space>
                  ) },
              ]}
            />
          )}
        </Card>
      )}

      {/* ── New Appointment Modal ── */}
      <Modal
        title="New Appointment"
        open={newModal}
        onOk={() => newForm.submit()}
        onCancel={() => { setNewModal(false); newForm.resetFields(); }}
        okText="Create"
        destroyOnHidden>
        <Form form={newForm} layout="vertical" onFinish={handleCreate}
          initialValues={{ duration_minutes: 30 }}>
          <Form.Item name="patient" label="Patient"
            rules={[{ required: true, message: "Select a patient" }]}>
            <Select showSearch optionFilterProp="label"
              placeholder="Search patients…" options={patientOptions} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="date" label="Date"
                rules={[{ required: true, message: "Select a date" }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="time" label="Time"
                rules={[{ required: true, message: "Select a time" }]}>
                <TimePicker format="HH:mm" minuteStep={5} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="duration_minutes" label="Duration (minutes)"
            rules={[{ required: true, message: "Required" }]}>
            <InputNumber min={5} max={480} step={5} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="reason" label="Reason / Chief Complaint">
            <Input.TextArea rows={3} placeholder="What brings the patient in?" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Detail Modal ── */}
      <Modal
        title="Appointment Details"
        open={!!editing}
        onCancel={() => setEditing(null)}
        footer={null}
        width={640}
        destroyOnHidden>
        {editing && (
          <>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Patient">
                <Space>
                  <Text>{editing.patient_email ?? "—"}</Text>
                  {editing.patient_pin && (
                    <>
                      <Text code>{editing.patient_pin}</Text>
                      <Button size="small"
                        onClick={() => handleViewPatientHistory(editing.patient_pin)}>
                        View History
                      </Button>
                    </>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {dayjs(editing.created_at).format("MMM D, YYYY · h:mm A")}
              </Descriptions.Item>
            </Descriptions>
            <Form form={editForm} layout="vertical" onFinish={handleSave}>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="date" label="Date"
                    rules={[{ required: true, message: "Select a date" }]}>
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="time" label="Time"
                    rules={[{ required: true, message: "Select a time" }]}>
                    <TimePicker format="HH:mm" minuteStep={5} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="duration_minutes" label="Duration (minutes)"
                    rules={[{ required: true }]}>
                    <InputNumber min={5} max={480} step={5} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                    <Select>
                      <Option value="PENDING">Pending</Option>
                      <Option value="PENDING_APPROVAL">Awaiting Approval</Option>
                      <Option value="CONFIRMED">Confirmed</Option>
                      <Option value="COMPLETED">Completed</Option>
                      <Option value="CANCELLED">Cancelled</Option>
                      <Option value="REJECTED">Rejected</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="reason" label="Reason / Chief Complaint">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="notes" label={<span>Doctor's Notes <Tag color="orange" style={{ fontSize: 11 }}>PHI</Tag></span>}>
                <Input.TextArea rows={3} placeholder="Clinical notes (encrypted)" />
              </Form.Item>
              <Row justify="space-between">
                <Col>
                  <Space>
                    <Button type="primary" htmlType="submit">Save Changes</Button>
                    <Button icon={<CheckOutlined />} onClick={() => changeStatus(editing, "COMPLETED")}>
                      Mark Completed
                    </Button>
                    <Popconfirm title="Cancel this appointment?"
                      onConfirm={() => changeStatus(editing, "CANCELLED")}>
                      <Button danger icon={<CloseOutlined />}>Cancel Appointment</Button>
                    </Popconfirm>
                  </Space>
                </Col>
                <Col>
                  <Popconfirm title="Delete this appointment permanently?"
                    onConfirm={() => handleDelete(editing)}>
                    <Button danger type="text" icon={<DeleteOutlined />}>Delete</Button>
                  </Popconfirm>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Modal>

      {/* ── Approve Request Modal ── */}
      <Modal
        title="Confirm Appointment"
        open={!!approveModal}
        onOk={() => approveForm.submit()}
        onCancel={() => { setApproveModal(null); approveForm.resetFields(); }}
        okText="Confirm Appointment"
        destroyOnHidden>
        {approveModal && (
          <>
            <p style={{ marginBottom: 16 }}>
              Patient: <Text strong>{approveModal.patient_email ?? "—"}</Text><br />
              Requested: <Text type="secondary">
                {approveModal.preferred_date ?? "—"}
                {approveModal.preferred_time ? ` at ${approveModal.preferred_time.slice(0, 5)}` : ""}
              </Text>
            </p>
            <Form form={approveForm} layout="vertical" onFinish={handleApprove}>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="confirmed_date" label="Confirmed Date"
                    rules={[{ required: true, message: "Select a date" }]}>
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="confirmed_time" label="Confirmed Time"
                    rules={[{ required: true, message: "Select a time" }]}>
                    <TimePicker format="HH:mm" minuteStep={5} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="duration_minutes" label="Duration"
                rules={[{ required: true, message: "Select duration" }]}>
                <Select>
                  <Option value={15}>15 min</Option>
                  <Option value={30}>30 min</Option>
                  <Option value={45}>45 min</Option>
                  <Option value={60}>1 hour</Option>
                </Select>
              </Form.Item>
              <Form.Item name="notes" label="Notes to Patient (optional)">
                <Input.TextArea rows={2} placeholder="Any instructions or notes for the patient" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* ── Decline Request Modal ── */}
      <Modal
        title="Decline Appointment Request"
        open={!!rejectModal}
        onOk={handleReject}
        onCancel={() => { setRejectModal(null); setRejectNotes(""); }}
        okText="Decline"
        okButtonProps={{ danger: true }}
        destroyOnHidden>
        {rejectModal && (
          <>
            <p>
              Patient: <Text strong>{rejectModal.patient_email ?? "—"}</Text><br />
              Requested: <Text>
                {rejectModal.preferred_date ?? "—"}
                {rejectModal.preferred_time ? ` at ${rejectModal.preferred_time.slice(0, 5)}` : ""}
              </Text>
            </p>
            <Input.TextArea
              rows={3}
              placeholder="Optional notes for the patient (reason for declining)"
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
            />
          </>
        )}
      </Modal>

      {/* ── Patient History Drawer ── */}
      <Drawer
        open={!!historyDrawer}
        onClose={() => setHistoryDrawer(null)}
        size="large"
        title="Patient Medical History"
        destroyOnHidden>
        {historyDrawer && <PatientHistoryView data={historyDrawer} />}
      </Drawer>
    </>
  );
}
